import { CalendarEvent } from "@/pages/scheduling/types";
import { startOfWeek, endOfWeek, format, differenceInMinutes, getDay, addDays } from "date-fns";
import { isSickLeave, isRegularLeave, isPreviousYearLeave, isLeaveForOvertimeExclusion } from "./leaveUtils";
import { fromLocalISOString } from '@/utils/timezoneUtils';

// Weekly hour threshold (40 hours)
const WEEKLY_THRESHOLD = 40;

// Helper to check if a date is within the full week (Mon-Sun)
const isFullWeekDay = (date: Date): boolean => {
  // All days are valid (0-6, Sunday-Saturday)
  return true;
};

/**
 * Calculate overtime hours for a given month
 */
export const calculateOvertimeHours = (
  events: CalendarEvent[],
  monthStartDate: Date
): number => {
  if (!events || events.length === 0) return 0;

  console.log(`[OVERTIME CALC] Starting calculation with ${events.length} events for month starting ${format(monthStartDate, 'yyyy-MM-dd')}`);
  console.log('[OVERTIME CALC] ========== CALCULATION DETAILS ==========');

  // Group events by week (Monday-Sunday)
  const weeklyHours: Record<string, number> = {};
  const weeklyLeaveHours: Record<string, number> = {};
  const weeklyShifts: Record<string, CalendarEvent[]> = {};

  // Process each event and assign to the week it STARTS in
  for (const event of events) {
    const startDate = fromLocalISOString(event.start_date);
    const endDate = fromLocalISOString(event.end_date);
    
    // Calculate the week start (for grouping) - Start on Monday (weekStartsOn: 1)
    const weekStart = startOfWeek(startDate, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 6); // End on Sunday
    const weekKey = format(weekStart, 'yyyy-MM-dd');
    
    console.log(`[OVERTIME CALC] Processing shift: ${event.title || 'Untitled'} (${format(startDate, 'EEE yyyy-MM-dd HH:mm')} to ${format(endDate, 'EEE yyyy-MM-dd HH:mm')})`);
    console.log(`[OVERTIME CALC] Assigning to week: ${format(weekStart, 'yyyy-MM-dd')} to ${format(weekEnd, 'yyyy-MM-dd')}`);
    
    const durationMinutes = differenceInMinutes(endDate, startDate);
    const durationHours = Math.round(durationMinutes / 60); // Round to integer
    
    console.log(`[OVERTIME CALC] Shift duration: ${durationHours} hours`);
    
    // Initialize weekly arrays if needed
    if (!weeklyShifts[weekKey]) {
      weeklyShifts[weekKey] = [];
    }
    
    // Store the shift in its week
    weeklyShifts[weekKey].push(event);
    
    // Track regular hours
    weeklyHours[weekKey] = (weeklyHours[weekKey] || 0) + durationHours;
    
    // If this is any kind of leave that should be excluded, track it separately
    if (isLeaveForOvertimeExclusion(event.title) || (event.is_sick_leave === true)) {
      console.log(`[OVERTIME CALC] Shift marked as sick leave: ${durationHours} hours will be excluded`);
      weeklyLeaveHours[weekKey] = (weeklyLeaveHours[weekKey] || 0) + durationHours;
    } else {
      // Log other types of leave (which are now counted as normal hours)
      if (isRegularLeave(event.title)) {
        console.log(`[OVERTIME CALC] Regular leave: ${durationHours} hours WILL be counted in total`);
      } else if (isPreviousYearLeave(event.title)) {
        console.log(`[OVERTIME CALC] Previous year leave: ${durationHours} hours WILL be counted in total`);
      }
    }
    
    // Log whether this is a holiday shift for debugging (Sundays only for now)
    const dayOfWeek = getDay(startDate);
    const isSunday = dayOfWeek === 0; // Only Sundays are holidays
    if (isSunday) {
      console.log(`[OVERTIME CALC] Holiday shift detected on ${format(startDate, 'EEEE')} (${durationHours} hours)`);
    }
  }

  // Print a summary of the grouping
  console.log('[OVERTIME CALC] ========== WEEKLY GROUPING SUMMARY ==========');
  Object.keys(weeklyShifts).forEach(weekKey => {
    const shifts = weeklyShifts[weekKey];
    console.log(`[OVERTIME CALC] Week of ${weekKey} contains ${shifts.length} shifts`);
  });

  // Calculate total overtime
  let totalOvertime = 0;
  
  // Process each week
  console.log('[OVERTIME CALC] ========== WEEKLY OVERTIME CALCULATION ==========');
  Object.keys(weeklyHours).forEach(weekKey => {
    const totalHours = weeklyHours[weekKey];
    const leaveHours = weeklyLeaveHours[weekKey] || 0;
    const shiftsInWeek = weeklyShifts[weekKey] || [];
    
    // Subtract ONLY sick leave hours from the calculation
    const effectiveHours = totalHours - leaveHours;
    
    console.log(`[OVERTIME WEEK] Week ${weekKey}: Total=${totalHours.toFixed(2)}h, Sick Leave=${leaveHours.toFixed(2)}h, Effective=${effectiveHours.toFixed(2)}h`);
    console.log(`[OVERTIME WEEK] Shifts in this week (${shiftsInWeek.length}):`);
    
    // Log all shifts in this week for verification
    shiftsInWeek.forEach((shift, index) => {
      const startDate = fromLocalISOString(shift.start_date);
      const endDate = fromLocalISOString(shift.end_date);
      const duration = differenceInMinutes(endDate, startDate) / 60;
      console.log(`  Shift #${index + 1}: ${shift.title || 'Untitled'}: ${format(startDate, 'EEE yyyy-MM-dd HH:mm')} to ${format(endDate, 'EEE yyyy-MM-dd HH:mm')} (${duration.toFixed(2)}h)`);
    });
    
    // If working hours > threshold, count the difference as overtime
    if (effectiveHours > WEEKLY_THRESHOLD) {
      const weekOvertime = Math.round(effectiveHours - WEEKLY_THRESHOLD); // Round to integer
      totalOvertime += weekOvertime;
      console.log(`[OVERTIME WEEK] Added ${weekOvertime} hours overtime (${Math.round(effectiveHours)}h - ${WEEKLY_THRESHOLD}h threshold = ${weekOvertime}h)`);
    } else {
      console.log(`[OVERTIME WEEK] No overtime for this week (effective hours: ${Math.round(effectiveHours)} <= threshold: ${WEEKLY_THRESHOLD}h)`);
    }
  });
  
  console.log(`[OVERTIME CALC] ========== FINAL RESULT ==========`);
  console.log(`[OVERTIME CALC] Final overtime calculation: ${Math.round(totalOvertime)} hours`);
  return Math.round(totalOvertime); // Return integer value
};
